import { ERoleName } from '../../core/models/enums/role.enum';
import { CurrentAssessorProfileService } from '../../core/services/assessor/current-assessor-profile.service';
import { StateService } from '../../core/services/state/state.service';
import { ISchedulerSummaryDTO } from '../../pages/scheduler/shared/models/dto/scheduler-summary.dto';
import { SchedulerService } from '../../pages/scheduler/shared/services/scheduler.service';
import { AuditorProfileNoticeDialogService } from '../auditor-profile-notice-dialog/shared/services/auditor-profile-notice-dialog.service';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  EQimaIconName,
  EQimaPosition,
  EQimaTooltipStrategy,
  EQimaTooltipStyle,
  QimaIconModule,
  QimaTooltipDirectiveModule,
} from '@qima/ngx-qima';
import { StatusCodes } from 'http-status-codes';
import { filter, take } from 'rxjs/operators';

interface IMainNavigationItem {
  id: string;
  icon: EQimaIconName;
  labelKey: string;
  route: string;
  badgeCount?: number;
  dataCy: string;
  isActive?: boolean;
  isVisible: boolean;
  activeUrlPrefixes?: readonly string[];
}

@Component({
  selector: 'app-main-navigation',
  standalone: true,
  imports: [NgTemplateOutlet, TranslateModule, QimaIconModule, QimaTooltipDirectiveModule],
  templateUrl: './main-navigation.component.html',
  styleUrl: './main-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainNavigationComponent implements OnInit {
  public readonly qimaTooltipStyle: EQimaTooltipStyle = EQimaTooltipStyle.DARK;
  public readonly qimaTooltipPosition: EQimaPosition = EQimaPosition.RIGHT;
  public readonly qimaTooltipStrategy: EQimaTooltipStrategy = EQimaTooltipStrategy.ON_HOVER;
  public readonly isAuditor = signal(false);
  public readonly navigationItems: IMainNavigationItem[] = [
    {
      id: 'bookings',
      icon: EQimaIconName.LIST,
      labelKey: 'MAIN_NAV.BOOKINGS',
      route: '/home',
      dataCy: 'main-navigation-item-bookings',
      isVisible: true,
    },
    {
      id: 'scheduling-queue',
      icon: EQimaIconName.TIME,
      labelKey: 'MAIN_NAV.SCHEDULING_QUEUE',
      route: '/orders/to-be-scheduled',
      badgeCount: 0,
      dataCy: 'main-navigation-item-scheduling-queue',
      isVisible: false,
    },
    {
      id: 'calendar',
      icon: EQimaIconName.CALENDAR,
      labelKey: 'MAIN_NAV.CALENDAR',
      route: '/calendar',
      dataCy: 'main-navigation-item-calendar',
      isVisible: true,
    },
    {
      id: 'auditors',
      icon: EQimaIconName.MY_TEAM,
      labelKey: 'MAIN_NAV.AUDITORS',
      route: '/auditors',
      dataCy: 'main-navigation-item-auditors',
      activeUrlPrefixes: ['/auditor'],
      isVisible: true,
    },
  ];

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);
  private readonly _schedulerService = inject(SchedulerService);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _stateService = inject(StateService);
  private readonly _currentAssessorProfileService = inject(CurrentAssessorProfileService);
  private readonly _auditorProfileNoticeDialog = inject(AuditorProfileNoticeDialogService);

  public constructor() {
    effect((): void => {
      const selectedRole = this._stateService.selectedRole();
      const activeRoles =
        selectedRole != null && selectedRole.trim() !== '' ? [selectedRole] : (this._stateService.currentUser()?.roles ?? []);

      untracked((): void => this._onActiveRolesChange(activeRoles));
    });
  }

  /**
   * Navigates for the clicked main nav item.
   * @param {IMainNavigationItem} item - Clicked navigation item.
   * @returns {void}
   */
  public onNavigationItemClick(item: IMainNavigationItem): void {
    if (item.id === 'auditors' && this.isAuditor()) {
      this._navigateAuditorMarketplaceForCurrentUser();

      return;
    }

    void this._router.navigateByUrl(item.route);
  }

  public ngOnInit(): void {
    this._syncActiveNavigationItems(this._router.url);

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event): void => {
        this._syncActiveNavigationItems(event.urlAfterRedirects);
      });
  }

  /**
   * Opens the current auditor profile, or the missing-profile dialog.
   * @returns {void}
   */
  private _navigateAuditorMarketplaceForCurrentUser(): void {
    this._currentAssessorProfileService
      .profileId$()
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (profileId): void => {
          if (profileId === null) {
            this._auditorProfileNoticeDialog.openNotCreated();

            return;
          }

          void this._router.navigate(['/auditor', profileId, 'overview']);
        },
        error: (): void => {
          void this._router.navigate(['/error'], { queryParams: { code: StatusCodes.INTERNAL_SERVER_ERROR } });
        },
      });
  }

  private _onActiveRolesChange(activeRoles: readonly string[]): void {
    this.isAuditor.set(activeRoles.length > 0 && activeRoles.every((role): boolean => role === ERoleName.AUDITOR));
    this.navigationItems.forEach((item): void => {
      item.isVisible =
        item.id !== 'scheduling-queue' ||
        activeRoles.some((role): boolean => role !== ERoleName.AUDITOR && role !== ERoleName.TECHNICAL_REVIEWER);
    });

    if (this._isSchedulingQueueVisible()) {
      this._getSchedulerSummary();
    }

    this._changeDetectorRef.markForCheck();
  }

  private _syncActiveNavigationItems(url: string): void {
    const path = url.split('?')[0]?.split('#')[0] ?? '';

    this.navigationItems.forEach((item): void => {
      const isMatchesRoute = path.startsWith(item.route);
      const isMatchesPrefix = item.activeUrlPrefixes?.some((prefix): boolean => path.startsWith(prefix)) ?? false;

      item.isActive = isMatchesRoute || isMatchesPrefix;
    });

    this._changeDetectorRef.markForCheck();
  }

  private _getSchedulerSummary(): void {
    this._schedulerService
      .getSchedulerSummary$(false)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((summary: ISchedulerSummaryDTO): void => {
        const schedulingQueueItem = this.navigationItems.find((item): boolean => item.id === 'scheduling-queue');

        if (schedulingQueueItem === undefined) {
          return;
        }

        schedulingQueueItem.badgeCount = summary.waitingForFirstClient + summary.waitingForScheduling + summary.behindSchedule;
        this._changeDetectorRef.markForCheck();
      });
  }

  private _isSchedulingQueueVisible(): boolean {
    return this.navigationItems.some((item): boolean => item.id === 'scheduling-queue' && item.isVisible);
  }
}
